import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const field =
  "w-full bg-[var(--field-bg-soft)] border border-line text-ink font-sans text-sm px-4 py-3 rounded-lg focus:border-gold focus:outline-none transition-colors placeholder:text-ink-dim";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return String(detail);
}

export default function AuthModal({ open, onOpenChange, initialMode = "login" }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (open) setMode(initialMode);
  }, [open, initialMode]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login({ email: form.email, password: form.password });
      else await register(form);
      toast.success(mode === "login" ? "Welcome back." : "Account created. Top up to buy instantly.");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="auth-modal" className="bg-surface border border-gold/25 text-ink max-w-md p-0 gap-0 rounded-2xl overflow-hidden">
        <div className="p-8">
          <DialogHeader className="text-left space-y-2 mb-6">
            <div className="overline">Private Access</div>
            <DialogTitle className="font-serif text-4xl tracking-tight text-ink">
              {mode === "login" ? "Sign in" : "Create account"}
            </DialogTitle>
            <DialogDescription className="text-ink-dim text-sm">
              Top up your wallet and acquire domains instantly.
            </DialogDescription>
          </DialogHeader>

          <button
            onClick={google}
            data-testid="google-signin"
            className="w-full flex items-center justify-center gap-3 bg-white text-black font-sans font-semibold text-sm py-3 rounded-lg hover:bg-white/90 transition-colors mb-5"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </button>

          <div className="flex items-center gap-4 mb-5">
            <span className="h-px flex-1 bg-[var(--line)]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-dim">or</span>
            <span className="h-px flex-1 bg-[var(--line)]" />
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
            {mode === "register" && (
              <input className={field} placeholder="Full name" value={form.name} onChange={set("name")} data-testid="auth-name" />
            )}
            <input className={field} type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="auth-email" required />
            <input className={field} type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={set("password")} data-testid="auth-password" required />
            <button type="submit" disabled={loading} data-testid="auth-submit" className="w-full bg-gold text-bg font-sans font-bold text-sm py-3.5 rounded-lg hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] transition-shadow flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 size={15} className="animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="mt-5 text-center text-sm text-ink-dim">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              data-testid="auth-toggle"
              className="text-gold hover-underline"
            >
              {mode === "login" ? "Create account" : "Sign in"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
